/**
 * Unified invoice line-type architecture.
 *
 * Every invoice line ultimately belongs to one of these types.
 * New types can be registered here without rewriting the editor.
 */

export const INVOICE_LINE_TYPE = Object.freeze({
  LABOUR: "labour",
  MATERIAL: "material",
  SERVICE: "service",
  CREDIT: "credit",
  DISCOUNT: "discount",
  ADJUSTMENT: "adjustment",
});

/**
 * Metadata for each line type. `enabled` gates Add Line Item categories;
 * disabled types are reserved for future phases.
 */
export const INVOICE_LINE_TYPE_REGISTRY = Object.freeze({
  [INVOICE_LINE_TYPE.SERVICE]: Object.freeze({
    id: INVOICE_LINE_TYPE.SERVICE,
    label: "Service",
    description: "Add a billable service not tied to labour or materials.",
    enabled: true,
    importable: false,
    contributesToSubtotal: true,
    subtotalBucket: "services",
  }),
  [INVOICE_LINE_TYPE.LABOUR]: Object.freeze({
    id: INVOICE_LINE_TYPE.LABOUR,
    label: "Labour",
    description: "Import uninvoiced labour from time logs, or add manual labour hours.",
    enabled: true,
    importable: true,
    contributesToSubtotal: true,
    subtotalBucket: "labour",
  }),
  [INVOICE_LINE_TYPE.MATERIAL]: Object.freeze({
    id: INVOICE_LINE_TYPE.MATERIAL,
    label: "Material",
    description: "Import uninvoiced materials from the project.",
    enabled: true,
    importable: true,
    contributesToSubtotal: true,
    subtotalBucket: "materials",
  }),
  [INVOICE_LINE_TYPE.CREDIT]: Object.freeze({
    id: INVOICE_LINE_TYPE.CREDIT,
    label: "Credit",
    description: "Apply a credit to the invoice.",
    enabled: false,
    importable: false,
    contributesToSubtotal: true,
    subtotalBucket: "other",
  }),
  [INVOICE_LINE_TYPE.DISCOUNT]: Object.freeze({
    id: INVOICE_LINE_TYPE.DISCOUNT,
    label: "Discount",
    description: "Apply a line-level discount.",
    enabled: false,
    importable: false,
    contributesToSubtotal: true,
    subtotalBucket: "other",
  }),
  [INVOICE_LINE_TYPE.ADJUSTMENT]: Object.freeze({
    id: INVOICE_LINE_TYPE.ADJUSTMENT,
    label: "Adjustment",
    description: "Apply a line-level adjustment.",
    enabled: false,
    importable: false,
    contributesToSubtotal: true,
    subtotalBucket: "other",
  }),
});

/** Categories shown in the Add Line Item modal (enabled types only). */
export function getAddableInvoiceLineCategories() {
  return Object.values(INVOICE_LINE_TYPE_REGISTRY).filter((entry) => entry.enabled);
}

export function getInvoiceLineTypeMeta(lineType) {
  return (
    INVOICE_LINE_TYPE_REGISTRY[lineType] || {
      id: lineType,
      label: lineType || "Line",
      description: "",
      enabled: false,
      importable: false,
      contributesToSubtotal: true,
      subtotalBucket: "other",
    }
  );
}

export function isOperationalSourceLine(line) {
  return Boolean(line?.source_time_log_id || line?.source_usage_id);
}

/** Lines that should survive Refresh from Project (manual / service / etc.). */
export function isPreservedManualInvoiceLine(line) {
  if (!line) return false;
  if (isOperationalSourceLine(line)) return false;
  const type = line.line_type;
  return (
    type === INVOICE_LINE_TYPE.SERVICE ||
    type === INVOICE_LINE_TYPE.CREDIT ||
    type === INVOICE_LINE_TYPE.DISCOUNT ||
    type === INVOICE_LINE_TYPE.ADJUSTMENT ||
    // Option B manual labour: labour line with no source time log.
    (type === INVOICE_LINE_TYPE.LABOUR && !line.source_time_log_id) ||
    (!type && !isOperationalSourceLine(line))
  );
}

export function createInvoiceLineId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (char) => {
    const rand = (Math.random() * 16) | 0;
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}
